<template>
  <modal :show.sync="showModal" class="dark-modal password-reset-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ $t('users.resetPassword') }} - {{ user ? user.name : '' }}</h2>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- New Password Field -->
        <div class="field">
          <label class="label">
            {{ $t('users.newPassword') }}<span class="required-asterisk"> *</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.password') }}
              </div>
            </popover>
          </label>
          <div class="control has-icons-right">
            <input
              v-model="formData.newPassword"
              class="input"
              :type="showNewPassword ? 'text' : 'password'"
              :class="{ 'is-danger': validationErrors.newPassword }"
              :placeholder="$t('users.enterNewPassword')"
              autocomplete="new-password"
              maxlength="60"
              required
              @blur="validatePassword"
              @input="handleNewPasswordInput"
            >
            <span
              class="icon is-small is-right"
              style="cursor: pointer; pointer-events: auto;"
              @click="toggleNewPasswordVisibility"
              :title="showNewPassword ? $t('users.hidePassword') : $t('users.showPassword')"
            >
              <octicon :icon="showNewPassword ? eyeClosed : eye" />
            </span>
          </div>
          <p v-if="validationErrors.newPassword" class="help is-danger">
            {{ validationErrors.newPassword }}
          </p>
        </div>

        <!-- Confirm Password Field -->
        <div class="field">
          <label class="label">
            {{ $t('users.confirmPassword') }}<span class="required-asterisk"> *</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.confirmPassword') }}
              </div>
            </popover>
          </label>
          <div class="control has-icons-right">
            <input
              v-model="formData.confirmPassword"
              class="input"
              :type="showConfirmPassword ? 'text' : 'password'"
              :class="{ 'is-danger': validationErrors.confirmPassword }"
              :placeholder="$t('users.confirmPassword')"
              autocomplete="new-password"
              maxlength="60"
              required
              @blur="validatePasswordConfirmation"
              @input="handleConfirmPasswordInput"
            >
            <span
              class="icon is-small is-right"
              style="cursor: pointer; pointer-events: auto;"
              @click="toggleConfirmPasswordVisibility"
              :title="showConfirmPassword ? $t('users.hidePassword') : $t('users.showPassword')"
            >
              <octicon :icon="showConfirmPassword ? eyeClosed : eye" />
            </span>
          </div>
          <p v-if="validationErrors.confirmPassword" class="help is-danger">
            {{ validationErrors.confirmPassword }}
          </p>
        </div>

        <!-- Password Actions -->
        <div class="password-actions">
          <button
            type="button"
            class="button action-button"
            @click="generatePassword"
          >
            <span class="icon">
              <octicon :icon="plus" />
            </span>
            <span>{{ $t('users.generateRandomPassword') }}</span>
          </button>
          <button
            type="button"
            class="button action-button"
            @click="copyPassword"
            :disabled="!formData.newPassword"
          >
            <span class="icon">
              <octicon :icon="pencil" />
            </span>
            <span>{{ $t('users.copyPassword') }}</span>
          </button>
        </div>

        <!-- Form Actions -->
        <div class="modal-footer">
          <button type="button" class="button cancel-button" @click.prevent="handleCancel" :disabled="isValidating">
            {{ $t('cancel') }}
          </button>
          <button
            type="submit"
            class="button is-primary"
            :class="{ 'is-loading': isValidating }"
            :disabled="!isFormValid || isValidating"
          >
            {{ $t('users.resetPassword') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cancel Confirmation Modal -->
    <modal :show.sync="showCancelConfirm" class="cancel-confirm-modal">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ $t('indexes.cancelTitle') }}</h3>
        </div>
        <div class="confirm-body">
          <p>{{ $t('indexes.cancelMessage') }}</p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="showCancelConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmCancel"
          >
            {{ $t('confirm') }}
          </button>
        </div>
      </div>
    </modal>
  </modal>
</template>

<script>
import { Modal, Popover } from '@cyradar/ui'
import { eye, eyeClosed, plus, pencil, info } from 'octicons-vue'
import { validatePasswordFormat, validatePasswordConfirmation, generateValidPassword } from '@/utils/passwordValidation'

export default {
  name: 'PasswordResetModal',
  components: {
    Modal,
    Popover
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      formData: {
        newPassword: '',
        confirmPassword: ''
      },
      validationErrors: {
        newPassword: '',
        confirmPassword: ''
      },
      isValidating: false,
      showNewPassword: false,
      showConfirmPassword: false,
      showCancelConfirm: false
    }
  },
  computed: {
    showModal: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      }
    },
    eye() {
      return eye
    },
    eyeClosed() {
      return eyeClosed
    },
    plus() {
      return plus
    },
    pencil() {
      return pencil
    },
    info() {
      return info
    },
    isFormValid() {
      const hasNoErrors = !this.validationErrors.newPassword && !this.validationErrors.confirmPassword
      const hasValues = this.formData.newPassword && this.formData.confirmPassword
      return hasNoErrors && hasValues
    }
  },
  watch: {
    show(newValue) {
      if (!newValue) {
        this.resetForm()
      }
    }
  },
  methods: {
    validatePassword() {
      if (!this.$t) {
        return false
      }
      const result = validatePasswordFormat(this.formData.newPassword, this.$t.bind(this))
      this.validationErrors.newPassword = result.error
      return result.isValid
    },
    validatePasswordConfirmation() {
      if (!this.$t) {
        return false
      }
      const result = validatePasswordConfirmation(this.formData.newPassword, this.formData.confirmPassword, this.$t.bind(this))
      this.validationErrors.confirmPassword = result.error
      return result.isValid
    },

    /**
     * Clears validation error for a specific field
     */
    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    /**
     * Validates all form fields
     */
    validateForm() {
      const passwordValid = this.validatePassword()
      const confirmPasswordValid = this.validatePasswordConfirmation()
      return passwordValid && confirmPasswordValid
    },

    /**
     * Resets form to default values
     */
    resetForm() {
      this.formData = {
        newPassword: '',
        confirmPassword: ''
      }
      this.validationErrors = {
        newPassword: '',
        confirmPassword: ''
      }
      this.showNewPassword = false
      this.showConfirmPassword = false
    },

    /**
     * Toggles visibility of the new password field
     */
    toggleNewPasswordVisibility() {
      this.showNewPassword = !this.showNewPassword
    },

    /**
     * Toggles visibility of the confirm password field
     */
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword
    },

    /**
     * Generates a random password and populates both input fields
     * Uses shared utility to ensure generated password passes all validation
     */
    generatePassword() {
      const generatedPassword = generateValidPassword()
      this.formData.newPassword = generatedPassword
      this.formData.confirmPassword = generatedPassword

      // Clear any existing validation errors since generated password is guaranteed to be valid
      this.clearFieldError('newPassword')
      this.clearFieldError('confirmPassword')
    },
    /**
     * Copies the current password to the clipboard
     */
    async copyPassword() {
      if (!this.formData.newPassword) {
        return
      }

      try {
        await navigator.clipboard.writeText(this.formData.newPassword)
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('users.passwordCopiedSuccess')
        })
      } catch (error) {
        // Fallback for older browsers
        try {
          const textArea = document.createElement('textarea')
          textArea.value = this.formData.newPassword
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)

          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: this.$t('users.passwordCopiedSuccess')
          })
        } catch (fallbackError) {
          this.$store.dispatch('NOTIFY', {
            type: 'error',
            text: this.$t('users.passwordCopyError')
          })
        }
      }
    },

    async handleSubmit() {
      if (this.isValidating) {
        return
      }

      this.isValidating = true

      try {
        const isValid = this.validateForm()

        if (!isValid) {
          this.$store.dispatch('NOTIFY', {
            type: 'warning',
            text: this.$t('users.validation.formHasErrors')
          })
          return
        }

        if (!this.formData.newPassword || !this.user) {
          return
        }

        const payload = {
          new_password: this.formData.newPassword
        }

        const body = await this.$http.patch(`/api/v1/users/${this.user.id}/reset-password`, payload)

        if (!body || !body.data) {
          return
        }

        this.handleClose()
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message || this.$t('users.passwordResetSuccess')
        })

        this.$emit('password-reset')
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('users.passwordResetError')
        })
      } finally {
        this.isValidating = false
      }
    },

    /**
     * Handles new password input events
     */
    handleNewPasswordInput() {
      // Check if input contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(this.formData.newPassword)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        this.formData.newPassword = this.formData.newPassword.replace(/[^\x00-\x7F]/g, '')
        this.validationErrors.newPassword = this.$t('users.validation.passwordAsciiOnly')
      } else {
        this.clearFieldError('newPassword')
      }

      // Limit to 60 characters
      const cleaned = this.formData.newPassword.substring(0, 60)
      if (this.formData.newPassword !== cleaned) {
        this.formData.newPassword = cleaned
      }

      // Re-validate confirm password if it has a value
      if (this.formData.confirmPassword) {
        this.validatePasswordConfirmation()
      }
    },

    /**
     * Handles confirm password input events
     */
    handleConfirmPasswordInput() {
      // Check if input contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(this.formData.confirmPassword)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        this.formData.confirmPassword = this.formData.confirmPassword.replace(/[^\x00-\x7F]/g, '')
        this.validationErrors.confirmPassword = this.$t('users.validation.passwordAsciiOnly')
      } else {
        this.clearFieldError('confirmPassword')
      }

      // Limit to 60 characters
      const cleaned = this.formData.confirmPassword.substring(0, 60)
      if (this.formData.confirmPassword !== cleaned) {
        this.formData.confirmPassword = cleaned
      }

      // Re-validate password confirmation
      if (this.formData.newPassword) {
        this.validatePasswordConfirmation()
      }
    },

    /**
     * Handles cancel button click - shows confirmation dialog
     */
    handleCancel() {
      this.showCancelConfirm = true
    },

    /**
     * Confirms cancellation and closes the modal
     */
    confirmCancel() {
      this.showCancelConfirm = false
      this.handleClose()
    },

    /**
     * Handles modal close
     */
    handleClose() {
      this.$emit('update:show', false)
    }
  }
}
</script>

<style lang="scss">
// Password actions specific styles
.password-reset-modal {
  .password-actions {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;

    .action-button {
      background-color: #2a2d36;
      border: 1px solid #404553;
      color: #fff;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover:not(:disabled) {
        border-color: #1F6FF4;
        background-color: rgba(31, 111, 244, 0.1);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .icon {
        display: flex;
        align-items: center;
      }
    }
  }
}

// Cancel Confirmation Modal Styles
.cancel-confirm-modal {
  .confirm-box {
    background-color: #1a1d2e;
    border-radius: 8px;
    padding: 2rem;
    max-width: 500px;

    .confirm-header {
      margin-bottom: 1.5rem;

      h3 {
        color: #fff;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .confirm-body {
      margin-bottom: 2rem;

      p {
        color: rgba(255, 255, 255, 0.8);
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }

    .confirm-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;

      .button {
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;

        &.cancel-button {
          background-color: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #fff;

          &:hover {
            border-color: rgba(255, 255, 255, 0.5);
          }
        }

        &.is-primary {
          background-color: #1F6FF4;
          border: none;
          color: #fff;

          &:hover {
            background-color: #1557c7;
          }
        }
      }
    }
  }
}
</style>
